import { AnalysisType, ProjectType } from "./ProjectTypes";

// The row shape of the Notifications page and the `data` of each type.
// Mirrors node-services-v2/src/types/NotificationTypes.ts.

export enum NotificationType {
    OWNER_ASSIGNED = "owner_assigned",
    COLLABORATOR_ADDED = "collaborator_added",
    COLLABORATOR_REMOVED = "collaborator_removed",
    ANALYSIS_SUBMITTED = "analysis_submitted",
    ANALYSIS_UPDATED = "analysis_updated",
    CHANGE_REQUESTED = "change_requested",
    ANALYSIS_COMPLETED = "analysis_completed",
    OUTPUT_UPDATED = "output_updated",
    PROJECT_CLOSED = "project_closed",
}

/** What every project notification carries: the project right after the event, and who caused it. */
export interface ProjectNotificationData {
    projectId: number;
    projectName: string;
    projectType: ProjectType;
    vendorKey?: string;
    vendorName?: string;
    /** The vendor's logo URL. */
    vendorHero?: string;
    actorId: number;
    actorName: string;
}

export interface OwnerAssignedData extends ProjectNotificationData {
    ownerId: number;
    ownerName: string;
}

export interface CollaboratorAddedData extends ProjectNotificationData {
    collaboratorId: number;
    collaboratorName: string;
}

export interface CollaboratorRemovedData extends ProjectNotificationData {
    collaboratorId: number;
    collaboratorName: string;
}

export interface AnalysisSubmittedData extends ProjectNotificationData {
    analysisVersion: number;
    analysisType: AnalysisType;
    submittedDate: string;
}

export interface AnalysisUpdatedData extends ProjectNotificationData {
    analysisVersion: number;
    analysisType: AnalysisType;
    updatedDate: string;
}

export interface ChangeRequestedData extends ProjectNotificationData {
    analysisVersion: number;
    analysisType: AnalysisType;
    message: string;
}

export interface AnalysisCompletedData extends ProjectNotificationData {
    analysisVersion: number;
    analysisType: AnalysisType;
    completedDate: string;
}

export interface OutputUpdatedData extends ProjectNotificationData {
    analysisVersion: number;
    analysisType: AnalysisType;
    updatedDate: string;
}

export type ProjectOutcome = "signed" | "cancelled" | "completed";

export interface ProjectClosedData extends ProjectNotificationData {
    analysisVersion: number;
    outcome: ProjectOutcome;
    closedDate: string;
}

export interface NotificationDataByType {
    [NotificationType.OWNER_ASSIGNED]: OwnerAssignedData;
    [NotificationType.COLLABORATOR_ADDED]: CollaboratorAddedData;
    [NotificationType.COLLABORATOR_REMOVED]: CollaboratorRemovedData;
    [NotificationType.ANALYSIS_SUBMITTED]: AnalysisSubmittedData;
    [NotificationType.ANALYSIS_UPDATED]: AnalysisUpdatedData;
    [NotificationType.CHANGE_REQUESTED]: ChangeRequestedData;
    [NotificationType.ANALYSIS_COMPLETED]: AnalysisCompletedData;
    [NotificationType.OUTPUT_UPDATED]: OutputUpdatedData;
    [NotificationType.PROJECT_CLOSED]: ProjectClosedData;
}

/** A page of notifications, newest first; `next` continues it, null when there is nothing older. */
export interface NotificationPage {
    data: UserNotification[];
    next: number | null;
}

/**
 * A notification as the API returns it. `text` is the event in one sentence
 * for the reader, written by the server; `data` carries the project the row
 * links to and shows as its title.
 */
export type UserNotification = {
    [T in NotificationType]: {
        id: number;
        type: T;
        data: NotificationDataByType[T];
        text: string;
        recordCreationDate: string;
        /** When the user saw it; null while it is new. */
        readDate: string | null;
    };
}[NotificationType];

/**
 * What the nav polls: how many notifications the user has not seen, the
 * newest id (null with no notification yet), and the ones newer than the
 * id the nav asked about, newest first, for the cards shown as they arrive.
 */
export interface NotificationStatus {
    unread: number;
    latestId: number | null;
    fresh: UserNotification[];
}

/** A user's say over their emails: the master switch and the types turned off. */
export interface NotificationSettings {
    emailEnabled: boolean;
    disabledTypes: NotificationType[];
}

/** The per-type toggles of the profile page, in display order, each with when it fires. */
export const NOTIFICATION_TYPE_OPTIONS: {
    type: NotificationType;
    label: string;
    description: string;
}[] = [
    {
        type: NotificationType.ANALYSIS_SUBMITTED,
        label: "Analysis requested",
        description: "Someone submits one of your projects for analysis.",
    },
    {
        type: NotificationType.ANALYSIS_UPDATED,
        label: "Analysis updated",
        description: "Someone adds information to a project while its analysis is in progress.",
    },
    {
        type: NotificationType.CHANGE_REQUESTED,
        label: "Changes requested",
        description: "Our team needs changes before an analysis can continue.",
    },
    {
        type: NotificationType.ANALYSIS_COMPLETED,
        label: "Analysis ready",
        description: "The results of one of your projects are ready.",
    },
    {
        type: NotificationType.OUTPUT_UPDATED,
        label: "Results updated",
        description: "Our team updates the results of one of your projects.",
    },
    {
        type: NotificationType.PROJECT_CLOSED,
        label: "Project closed",
        description: "A deal is signed or cancelled, or an RFx is completed.",
    },
    {
        type: NotificationType.OWNER_ASSIGNED,
        label: "Owner assigned",
        description: "A project you own is given to someone, or one is given to you.",
    },
    {
        type: NotificationType.COLLABORATOR_ADDED,
        label: "Project shared",
        description: "A project is shared with you, or one of yours with someone.",
    },
    {
        type: NotificationType.COLLABORATOR_REMOVED,
        label: "Access removed",
        description: "You are removed from a project, or someone from one of yours.",
    },
];

export enum DeliveryStatus {
    QUEUED = "queued",
    SENT = "sent",
    FAILED = "failed",
    ABANDONED = "abandoned",
}

/** A delivery as the super admin dashboard lists it. */
export interface NotificationDelivery {
    id: number;
    organizationId: number;
    organizationName: string;
    type: NotificationType;
    projectId: number;
    projectName: string;
    recipientId: number;
    recipientName: string;
    recipientEmail: string;
    channel: string;
    status: DeliveryStatus;
    attempts: number;
    lastError: string | null;
    nextAttemptDate: string;
    sentDate: string | null;
    recordCreationDate: string;
}

export interface NotificationDeliveryPage {
    data: NotificationDelivery[];
    next: number | null;
}
